import {Collapse} from "bootstrap";
import {calendarObjects} from "./calendar";
import axios, {AxiosError, AxiosResponse} from "axios";
import {initTooltips, sprintf} from "./functions";
import {startLoading, stopLoading} from "./loaders";
import {validateInput} from "./validators";
import validate = WebAssembly.validate;

type StepName = 'type' | 'calendar' | 'info';

export interface CollapseGroup {
	element: HTMLElement,
	collapse: Collapse
}

export interface Step {
	sectionElement: HTMLElement,
	hasError: boolean,
	select: CollapseGroup,
	result: CollapseGroup
}

export interface SubTypeData {
	id: number,
	type: number,
	name: string,
	description: string,
	limitOverride: boolean,
	singlePlayerInput: boolean,
	slotMin: number | null,
	slotMax: number | null,
	mergeSlots: number | null,
	slotPreset: string | null,
}

export interface TypeData {
	id: number,
	icon: string,
	name: string,
	slotLength: number,
	slotLimit: number,
	subtypes: { [index: number]: SubTypeData }
}

export default class BookingForm {
	private readonly form: HTMLFormElement;
	private steps: Map<StepName, Step> = new Map;
	private playersWrapper: HTMLElement;
	private playersSingleWrapper: HTMLDivElement;
	private playersSingleInput: HTMLInputElement;
	private playerLockWrapper: HTMLDivElement;
	private playerLockInput: HTMLInputElement;
	private subtypes: { [index: number]: CollapseGroup } = {};
	private subtypeWrapper: HTMLElement;
	private subtypeDescriptionsCollapse: { [index: number]: Collapse } = {};
	private errorsWrapper: HTMLElement;
	private errorsDateWrapper: HTMLElement;
	private errorsTypeWrapper: HTMLElement;

	private dateTimeDescription: HTMLDivElement;
	private infoDescription: HTMLDivElement;

	private readonly termsWrapper: HTMLDivElement;

	private limit: number = 0;
	private openable: boolean = true;
	private openableMin: number = 0;
	private openableDescription: string = '';
	private singleSlotCountInput: boolean = false;
	private minSlotCount: number = 0;
	private maxSlotCount: number = 0;
	private slotPreset: string = '1';

	constructor(form: HTMLFormElement) {
		this.form = form;
		this.playersWrapper = form.querySelector('.players');
		this.playersSingleWrapper = form.querySelector('.players-single');
		this.playersSingleInput = this.playersSingleWrapper.querySelector('input');
		this.playerLockWrapper = form.querySelector('.player-lock-wrapper');
		this.playerLockInput = this.playerLockWrapper.querySelector('input');

		['type', 'calendar', 'info'].forEach((type: StepName) => {
			const selectElement = document.getElementById(type + '-select');
			const resultElement = document.getElementById(type + '-result');
			this.steps.set(type, {
				hasError: false,
				sectionElement: selectElement.parentElement,
				select: {
					element: selectElement,
					collapse: new Collapse(selectElement, {toggle: false})
				},
				result: {
					element: resultElement,
					collapse: new Collapse(resultElement, {toggle: false})
				}
			});
		});
		console.log(this.steps);

		this.dateTimeDescription = this.steps.get('calendar').select.element.querySelector('.description');
		this.infoDescription = this.steps.get('info').select.element.querySelector('.description');

		this.termsWrapper = this.steps.get('info').select.element.querySelector('.terms-and-conditions');

		(document.querySelectorAll('.subtype-select') as NodeListOf<HTMLElement>).forEach(subtype => {
			const id = parseInt(subtype.dataset.id);
			this.subtypes[id] = {
				element: subtype,
				collapse: new Collapse(subtype, {toggle: false})
			};
		});

		this.subtypeWrapper = document.querySelector('.subtype-info');

		const subtypeDescriptions: NodeListOf<HTMLElement> = document.querySelectorAll('.subtype-description');
		subtypeDescriptions.forEach(elem => {
			const id = parseInt(elem.dataset.id);
			this.subtypeDescriptionsCollapse[id] = new Collapse(elem, {toggle: false});
		});

		this.errorsWrapper = document.getElementById('errors');
		this.errorsDateWrapper = this.steps.get('calendar').select.element.querySelector('.errors');
		this.errorsTypeWrapper = this.steps.get('type').select.element.querySelector('.errors');

		this.initEventListeners();

		this.triggerTypeChange();
		this.triggerSubTypeChange();
	}

	addError(key: string, error: string) {
		if (key === 'date' || key === 'time') {
			this.errorsDateWrapper.appendChild(this.errorAlert(error));
			this.triggerStepError('calendar');
			return;
		}
		if (key === 'type') {
			this.errorsTypeWrapper.appendChild(this.errorAlert(error));
			this.triggerStepError('type');
			return;
		}
		if (key === 'playerCount') {
			const errorMessage: HTMLDivElement = this.playersSingleWrapper.querySelector('.invalid-feedback');
			errorMessage.innerText = error;
			this.playersSingleInput.classList.add('is-invalid');
			this.triggerStepError('calendar');
			return;
		}
		if (key.match(/term-\d+/)) {
			const termInput = document.getElementById(key) as HTMLInputElement;
			termInput.classList.add('is-invalid');
			const termFeedback = termInput.parentElement.querySelector('.invalid-feedback') as HTMLDivElement | undefined;
			if (termFeedback) {
				termFeedback.innerHTML = '';
				console.log(termFeedback, error);
				termFeedback.innerHTML = error;
			}
			this.triggerStepError('info');
			return;
		}
		const test = parseInt(key);
		if (isNaN(test)) {
			// Input error
			const input = document.getElementById(key);
			if (input) {
				const parentSection = input.findParentElement('section');
				this.triggerStepError(parentSection.dataset.key as StepName);
				input.classList.add('is-invalid');
				const feedback: HTMLDivElement = input.parentElement.querySelector('.invalid-feedback');
				if (feedback) {
					const msg = document.createElement('div');
					msg.innerText = error;
					feedback.appendChild(msg);
				}
				return;
			}
		}
		// Global error
		this.errorsWrapper.appendChild(this.errorAlert(error));
	}

	errorAlert(error: string): HTMLDivElement {
		const alert = document.createElement('div');
		alert.classList.add('alert', 'alert-danger');
		alert.innerText = error;
		return alert;
	}

	setStep(stepName: StepName): void {
		if (stepName === 'info' && !this.validateCalendar()) {
			return;
		}
		this.steps.forEach((step, key) => {
			if (key === stepName) {
				step.select.collapse.show();
				step.result.collapse.hide();
			} else {
				step.select.collapse.hide();
				if (key === 'info') {
					if (this.isCalendarFilled()) {
						step.result.collapse.show();
					} else {
						step.result.collapse.hide();
					}
				} else {
					step.result.collapse.show();
				}
			}
		});

		// Scroll the current step into view
		setTimeout(() => { // Timeout to let all animations finish
			this.scrollToStep(stepName);
		}, 400);
	}

	scrollToStep(stepName: StepName) {
		const step = this.steps.get(stepName);
		window.scrollTo({
			behavior: 'smooth',
			top: step.sectionElement.offsetTop - 80,
		});
		/*step.sectionElement.scrollIntoView(
			{
				behavior: 'smooth',
				block: "start",
			}
		);*/
	}

	refreshTimePlayers() {
		this.toggleOpenable();
		if (this.singleSlotCountInput) {
			this.playersSingleWrapper.style.display = 'block';
			this.playersWrapper.style.display = 'none';
		} else {
			this.playersSingleWrapper.style.display = 'none';
			this.playersWrapper.style.display = 'block';

			// Update time players
			calendarObjects.forEach(/** @param {BookingCalendar} calendar */calendar => {
				calendar.getSelectedTimesInfo().forEach(({time, slots}) => {
					this.addTimePlayers(time, slots);
				});
			});
			// Remove old inputs
			document.querySelectorAll('.time-players').forEach(dom => {
				if (dom.classList.contains('found')) {
					dom.classList.remove('found');
					return;
				}
				dom.remove();
			});
			// Sort inputs
			let players: HTMLElement[] = Array.prototype.slice.call(document.querySelectorAll('.time-players'), 0);
			players.sort((a: HTMLElement, b: HTMLElement) => {
				const aTime = parseInt(a.dataset.time);
				const bTime = parseInt(b.dataset.time);
				return aTime - bTime;
			});
			players.forEach(player => {
				this.playersWrapper.appendChild(player);
			})
		}
	}

	private triggerStepError(stepName: StepName) {
		const step = this.steps.get(stepName);
		if (step.hasError) return;
		step.sectionElement.classList.add('is-invalid');
		step.hasError = true;
	}

	private clearStepError(stepName: StepName) {
		const step = this.steps.get(stepName);
		if (!step.hasError) return;
		step.sectionElement.classList.remove('is-invalid');
		step.hasError = false;
	}

	private updateCalendarInfo() {
		const selectInfo: HTMLElement = this.steps.get('calendar').result.element.querySelector('.selected');
		calendarObjects.forEach(calendar => {
			const date = calendar.getSelectedDateFormatted();
			const times = calendar.getSelectedTimes();
			console.log(date, times);
			if (date && times.length > 0) {
				selectInfo.innerText = date + ': ' + times.join(', ');
			} else {
				selectInfo.innerText = '';
			}
		});
	}

	/**
	 * Checks for any errors on the calendar section
	 * @private
	 */
	private validateCalendar(): boolean {
		let valid = true;

		if (!this.isCalendarFilled()) {
			this.addError('time', requiredTime);
			valid = false;
		}

		if (this.singleSlotCountInput && (!this.playersSingleInput.value || this.playersSingleInput.valueAsNumber < 1 || this.playersSingleInput.valueAsNumber < this.minSlotCount)) {
			this.addError('playerCount', missingPlayerCount);
			valid = false;
		}

		if (valid) {
			const parentSection = this.steps.get('calendar').select.element.findParentElement('section');
			if (parentSection) {
				parentSection.querySelector('h2').classList.remove('is-invalid');
			}
		}

		return valid;
	}

	private triggerTypeChange(input: HTMLInputElement | null = null): void {
		if (!input) {
			// Get selected checkbox
			input = this.getSelectedType();
			if (!input) {
				return;
			}
		}

		this.limit = parseInt(input.dataset.limit);
		const type = parseInt(input.value);
		const selectedSubtype: HTMLInputElement | undefined = this.subtypes[type].element.querySelector('input:checked');
		const subtypeCount = this.subtypes[type].element.querySelectorAll('input').length;
		this.openable = input.dataset.openable === '1';
		this.openableMin = parseInt(input.dataset.openableMin);
		this.openableDescription = input.dataset.openableDescription;
		this.toggleOpenable();
		this.steps.get('type').select.element.querySelector('.continue').classList.remove('d-none');
		(this.steps.get('type').result.element.querySelector('.selected') as HTMLElement).innerText = input.dataset.name;
		if (selectedSubtype && subtypeCount > 1) {
			(this.steps.get('type').result.element.querySelector('.selected') as HTMLElement).innerText += ' - ' + selectedSubtype.dataset.name;
		}

		this.slotPreset = '1';

		console.log(type);
		calendarObjects.forEach(calendar => {
			calendar.configuration.type = type;
			calendar.setPreset(this.slotPreset);
			calendar.reload();
		});

		this.fetchTerms(type);

		// Display subtype
		Object.values(this.subtypes).forEach(value => {
			value.collapse.hide();
		});
		this.subtypes[type].collapse.show();
		selectedSubtype.dispatchEvent(new Event('change'));
		const subTypeId = parseInt(selectedSubtype.value);
		this.toggleSubtypeDescription(subTypeId);
		this.fetchSubtype(subTypeId);
	}

	private triggerSubTypeChange(input: HTMLInputElement | null = null): void {
		if (!input) {
			// Get selected checkbox
			input = this.getSelectedSubType();
			if (!input) {
				return;
			}
		}

		const id = parseInt(input.value);
		const selectedType: HTMLInputElement = this.getSelectedType();
		const type = parseInt(selectedType.value);
		const subtypeCount = this.subtypes[type].element.querySelectorAll('input').length;
		if (subtypeCount > 1) {
			(this.steps.get('type').result.element.querySelector('.selected') as HTMLElement).innerText = selectedType.dataset.name + ' - ' + input.dataset.name;
		}
		this.toggleSubtypeDescription(id);
		this.fetchSubtype(id);

		if (input.dataset.slotPreset) {
			this.slotPreset = input.dataset.slotPreset;
		} else {
			this.slotPreset = '1';
		}

		calendarObjects.forEach(calendar => {
			calendar.configuration.subType = id;
			calendar.setPreset(this.slotPreset);
			calendar.reload();
		});

		this.singleSlotCountInput = 'singleInput' in input.dataset;
		this.minSlotCount = parseInt(input.dataset.min ?? '0');
		this.maxSlotCount = parseInt(input.dataset.max ?? '0');

		this.playersSingleInput.min = this.minSlotCount.toString();
		this.playersSingleInput.value = this.minSlotCount.toString();
		if (this.maxSlotCount > 0) {
			this.playersSingleInput.max = this.maxSlotCount.toString();
		} else {
			this.playersSingleInput.removeAttribute('max');
		}
		this.playersSingleInput.valueAsNumber = this.minSlotCount > 0 ? this.minSlotCount : this.limit;

		this.playersSingleInput.classList.remove('is-valid', 'is-invalid');
	}

	private getSelectedType(): HTMLInputElement | undefined {
		return this.steps.get('type').select.element.querySelector('input.type-input:checked');
	}

	private getSelectedSubType(): HTMLInputElement | undefined {
		const selectedType = this.getSelectedType();
		if (!selectedType) {
			return undefined;
		}

		const type = parseInt(selectedType.value);
		return this.subtypes[type].element.querySelector('input:checked');
	}

	private initEventListeners() {
		// Type and subtype select
		(this.steps.get('type').select.element.querySelectorAll('input.type-input') as NodeListOf<HTMLInputElement>).forEach(input => {
			input.addEventListener('change', () => {
				this.triggerTypeChange(input);
				this.clearStepError('type');
				this.checkAnyInvalidInput();
			});
		});
		(this.steps.get('type').select.element.querySelectorAll('input.subtype-input') as NodeListOf<HTMLInputElement>).forEach(input => {
			input.addEventListener('change', () => {
				this.triggerSubTypeChange(input);
				this.clearStepError('type');
				this.checkAnyInvalidInput();
			})
		});

		// Type - step change
		this.steps.get('type').select.element.querySelector('.next-step').addEventListener('click', () => {
			this.setStep('calendar');
		});
		this.steps.get('type').result.element.querySelector('.edit').addEventListener('click', () => {
			this.setStep('type');
		});

		// Calendar events
		const calendarNextStep = this.steps.get('calendar').select.element.querySelector('.next-step');
		document.addEventListener('calendarDateChange', () => {
			this.updateCalendarInfo();
		});
		document.addEventListener('calendarReload', () => {
			this.refreshTimePlayers();
			this.clearStepError('calendar');
		});
		document.addEventListener('calendarTimeChange', () => {
			if (this.isCalendarFilled()) {
				calendarNextStep.parentElement.classList.remove('d-none');
			} else {
				calendarNextStep.parentElement.classList.add('d-none');
			}
			this.updateCalendarInfo();

			this.refreshTimePlayers();
		});
		calendarNextStep.addEventListener('click', () => {
			this.setStep('info');
		});
		this.steps.get('calendar').result.element.querySelector('.edit').addEventListener('click', () => {
			this.setStep('calendar');
		});
		this.steps.get('info').result.element.querySelector('.edit').addEventListener('click', () => {
			this.setStep('info');
		});

		// Player count input validation
		this.playersSingleInput.addEventListener('input', () => {
			this.playersSingleInput.classList.remove('is-invalid');
			this.clearStepError('calendar');
			this.checkAnyInvalidInput();
		});
		this.playersSingleInput.addEventListener('change', () => {
			if (this.playersSingleInput.valueAsNumber < this.minSlotCount) {
				this.playersSingleInput.valueAsNumber = this.minSlotCount;
			} else if (this.maxSlotCount > 0 && this.playersSingleInput.valueAsNumber > this.maxSlotCount) {
				this.playersSingleInput.valueAsNumber = this.maxSlotCount;
			}
			validateInput(this.playersSingleInput);
		});

		this.initInfoEventListeners();

		// Form handler
		this.form.addEventListener('submit', e => {
			e.preventDefault();
			this.formSubmit();
		});
	}

	private initInfoEventListeners() {
		const step = this.steps.get('info');
		const inputs: NodeListOf<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement> = step.select.element.querySelectorAll('input:not(.listening),select:not(.listening),textarea:not(.listening)');

		inputs.forEach(input => {
			input.classList.add('listening');
			input.addEventListener('change', () => {
				if (validateInput(input)) {
					//input.classList.remove('is-invalid');
					//input.classList.add('is-valid');
				}
				if (this.isInfoValid()) {
					this.clearStepError('info');
				} else {
					this.triggerStepError('info');
				}
				this.checkAnyInvalidInput();
			});
		})
	}

	private isInfoValid(): boolean {
		const step = this.steps.get('info');
		const inputs: NodeListOf<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement> = step.select.element.querySelectorAll('input.is-invalid,select.is-invalid,textarea.is-invalid');

		return inputs.length === 0;
	}

	private checkAnyInvalidInput() {
		const inputs: NodeListOf<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement> = this.form.querySelectorAll('input.is-invalid,select.is-invalid,textarea.is-invalid');

		if (inputs.length === 0) {
			this.errorsWrapper.innerHTML = '';
		}
	}

	/**
	 * Handle form submit event
	 *
	 * Sends form using AJAX and if successful, redirects to the thank-you page. If there is an error, displays the error.
	 *
	 * @private
	 */
	private formSubmit() {
		const data = new FormData(this.form);
		startLoading();

		// Clear error messages
		this.errorsWrapper.innerHTML = '';
		this.errorsTypeWrapper.innerHTML = '';
		this.errorsDateWrapper.innerHTML = '';
		document.querySelectorAll('.is-invalid').forEach(elem => {
			elem.classList.remove('is-invalid');
		});
		document.querySelectorAll('.invalid-feedback').forEach(elem => {
			elem.innerHTML = '';
		});

		// Send booking data
		axios.post('/booking', data)
			.then((response: AxiosResponse<{ redirect?: string }>) => {
				stopLoading(true);
				if (response.data.redirect) {
					window.location.href = response.data.redirect;
				}
			})
			.catch((error: AxiosError<{ errors: { [index: string]: string | string[] } | string | string[] }>) => {
				const data = error.response.data;
				const errors = data.errors;
				console.log(error, data);
				stopLoading(false);

				// Display errors
				if (data.errors) {
					// Global error message = "Formulář obsahuje chyby"
					this.errorsWrapper.appendChild(this.errorAlert(errorAlertMsg));

					if ((typeof errors) === 'object') {
						// Add errors to sections
						Object.entries(errors).forEach(([key, error]) => {
							if ((typeof error) === 'string' && !(error instanceof Array)) {
								this.addError(key, error)
							} else if (error instanceof Array) {
								error.forEach(error1 => {
									this.addError(key, error1);
								});
							}
						});
					} else if (errors instanceof Array) {
						// Add global errors
						errors.forEach(errorMsg => {
							this.errorsWrapper.appendChild(this.errorAlert(errorMsg));
						});
					} else if ((typeof errors) === 'string' && !(errors instanceof Object)) {
						// Add global error
						this.errorsWrapper.appendChild(this.errorAlert(errors));
					}
				}

				// Scroll the first step with an error into view
				let found = false;
				this.steps.forEach((step, name) => {
					if (found || !step.hasError) return;
					found = true;
					this.scrollToStep(name);
				});
			})
	}

	/**
	 * Adds the "player count" input for each new time selected
	 * @param time Selected time string (value from the calendar)
	 * @param limit Player limit
	 * @private
	 */
	private addTimePlayers(time: string, limit: number) {
		let timeDom = document.getElementById('players-' + time);
		if (timeDom) {
			timeDom.classList.add('found');
			return;
		}
		const id = 'players-' + time + '-input';
		timeDom = document.createElement('div');
		timeDom.id = 'players-' + time;
		timeDom.classList.add('time-players', 'form-group', 'found');
		timeDom.dataset.time = time.replace(':', '');
		timeDom.setAttribute('data-time', timeDom.dataset.time);
		if (limit > 1) {
			timeDom.innerHTML = `<label for="${id}" class="form-label d-block">${sprintf(playersRangeLabel, time)}:</label>` +
				`<input required type="number" class="form-control" min="1" max="${limit}" id="${id}" name="players[${time}]" value="${limit}">` +
				`<div class="invalid-feedback"></div>`;
			const input = timeDom.querySelector('input');
			input.addEventListener('change', () => {
				validateInput(input);
			});
		} else {
			timeDom.innerHTML = `<div class="form-label">${sprintf(playersRangeLabel, time)}: <span class="value">${limit}</span></div>` +
				`<input type="hidden" id="${id}" name="players[${time}]" value="${limit}">`;
		}

		this.playersWrapper.appendChild(timeDom);

		if (!this.openable) {
			return;
		}

		// Maybe lock the openable checkbox
		const input = timeDom.querySelector('input');
		input.addEventListener('change', () => {
			let max: number = 0;
			this.playersWrapper.querySelectorAll('input').forEach(currInput => {
				max = Math.max(max, currInput.valueAsNumber);
			});
			if (max > this.openableMin) {
				this.playerLockInput.disabled = false;
			} else {
				this.playerLockInput.disabled = true;
				this.playerLockInput.checked = true;
			}
		});
	}

	/**
	 * Checks all global calendar objects if the date and time is selected.
	 * @private
	 */
	private isCalendarFilled(): boolean {
		let filled = false;
		calendarObjects.forEach(calendar => {
			const date = calendar.getSelectedDate();
			const times = calendar.getSelectedTimes();
			console.log(date, times);
			if (date && times.length > 0) {
				filled = true;
			}
		});
		return filled;
	}

	/**
	 * Show description for the current subtype and hide for others.
	 * @param id Selected subtype's id
	 * @private
	 */
	private toggleSubtypeDescription(id: number) {
		Object.values(this.subtypeDescriptionsCollapse).forEach(collapse => {
			collapse.hide();
		});
		this.subtypeDescriptionsCollapse[id].show();
	}

	/**
	 * Get subtype's information.
	 *
	 * Fetches additional descriptions for the calendar and info section.
	 *
	 * @param id
	 * @private
	 */
	private fetchSubtype(id: number) {
		axios.get(`/subtype/${id}/datetime`)
			.then((response: AxiosResponse<string>) => {
				this.dateTimeDescription.innerHTML = response.data;
			});
		axios.get(`/subtype/${id}/info`)
			.then((response: AxiosResponse<string>) => {
				this.infoDescription.innerHTML = response.data;
			});

		axios.get(`/subtype/${id}/fields`)
			.then((response: AxiosResponse<string>) => {
				this.subtypeWrapper.innerHTML = response.data;
				initTooltips(this.subtypeWrapper);
				this.initInfoEventListeners();
			})
			.catch(() => {
				this.subtypeWrapper.innerHTML = '';
			});
	}

	/**
	 * Hide or show the "additional players can join" checkbox
	 * @private
	 */
	private toggleOpenable(): void {
		this.playerLockWrapper.style.display = !this.openable || this.singleSlotCountInput ? 'none' : 'block';
		(this.playerLockWrapper.querySelector('#locked-description') as HTMLDivElement).innerText = this.openableMin > 0 ? this.openableDescription : '';
	}

	/**
	 * Fetch terms and conditions (HTML) for selected type
	 * @param id Selected type's id
	 * @private
	 */
	private fetchTerms(id: number): void {
		axios.get(`/booking/type/${id}/terms`)
			.then((response: AxiosResponse<string>) => {
				this.termsWrapper.innerHTML = response.data;
				initTooltips(this.termsWrapper);
				this.termsWrapper.querySelectorAll('input').forEach(input => {
					input.addEventListener('change', () => {
						input.classList.remove('is-invalid');
					});
				});
			})
	}

}